import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatGBP, formatDateUK } from "@/lib/api";
import { Z_STATUS, Z_STATUS_CLS } from "@/lib/z";
import { ArrowLeft, Plus } from "lucide-react";

function groupByDay(rows) {
  const days = {};
  rows.forEach((z) => {
    const k = `${z.trading_date}|${z.store_id}`;
    days[k] = days[k] || { date: z.trading_date, store_id: z.store_id, items: [], total: 0 };
    days[k].items.push(z);
    if (["submitted", "approved"].includes(z.status)) days[k].total += z.total_sales;
  });
  return Object.values(days).sort((a, b) => (a.date < b.date ? 1 : -1));
}

export default function ZClosingsPage() {
  const { activeStoreId, stores } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  useEffect(() => {
    const q = activeStoreId && activeStoreId !== "all" ? `?store_id=${activeStoreId}` : "";
    api.get(`/z-closings${q}`).then((r) => setRows(r.data.filter((z) => z.status !== "voided"))).catch(() => setRows([]));
  }, [activeStoreId]);
  const name = (sid) => stores.find((s) => s.id === sid)?.name || "";
  return (
    <div className="space-y-5" data-testid="z-closings-page">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/dashboard")} className="min-w-[44px] min-h-[44px] flex items-center justify-center text-navy/40" aria-label="Back"><ArrowLeft className="w-5 h-5" /></button>
        <h1 className="text-xl font-heading font-bold text-navy">Daily sales (Z reports)</h1>
      </div>
      <button onClick={() => navigate("/record-z")} className="w-full gm-btn-gold flex items-center justify-center gap-2" data-testid="new-z-btn"><Plus className="w-4 h-4" /> Record today's Z report</button>
      {rows === null && <p className="text-sm text-navy/50">Loading…</p>}
      {rows && rows.length === 0 && <p className="text-sm text-navy/50" data-testid="z-empty">No Z reports yet.</p>}
      {rows && groupByDay(rows).map((d) => (
        <div key={`${d.date}-${d.store_id}`} className="rounded-xl border border-navy/10 bg-white" data-testid={`z-day-${d.date}-${d.store_id}`}>
          <div className="flex justify-between items-baseline px-4 pt-3 pb-2 border-b border-navy/5">
            <div><p className="font-semibold text-navy text-sm">{formatDateUK(d.date)}</p><p className="text-xs text-navy/50">{name(d.store_id)}</p></div>
            <div className="text-right"><p className="text-[11px] text-navy/45">Day's sales</p><p className="font-mono font-bold text-navy" data-testid={`z-day-total-${d.date}-${d.store_id}`}>{formatGBP(d.total)}</p></div>
          </div>
          {d.items.map((z) => (
            <button key={z.id} onClick={() => navigate(`/z-closings/${z.id}`)} className="w-full flex items-center justify-between px-4 py-3 min-h-[52px] text-left" data-testid={`z-row-${z.id}`}>
              <span className="text-sm text-navy">{z.register_ref}</span>
              <span className="flex items-center gap-3">
                <span className="font-mono text-sm text-navy">{formatGBP(z.total_sales)}</span>
                <span className={`text-[11px] px-2 py-0.5 rounded-full ${Z_STATUS_CLS[z.status]}`} data-testid={`z-status-${z.id}`}>{Z_STATUS[z.status]}</span>
              </span>
            </button>
          ))}
        </div>
      ))}
    </div>
  );
}
